import { apiFetch } from './client';
import { config } from '../config';
import { legacyLogoUrl } from '../portal/logo';

// Fixture is the current match-centre state of a `match` activity. Mirrors the
// fixtureItem wire shape in internal/api/fixtures.go.
export interface Fixture {
  activity_id: string;
  starts_at: string; // RFC3339
  title: string;
  team_ids: string[];
  opponent_name: string;
  // Logo source (at most one): a matched Go org (slug), a legacy club id, or a
  // resolved public object key (uploaded crest OR the matched Go org's live
  // logo). The SPA renders via opponentLogoUrl().
  opponent_org_slug?: string;
  opponent_legacy_club_id?: number;
  // A crowd-sourced community club (core.community_club); its crest is resolved
  // server-side into opponent_logo_key, so display needs no special-casing.
  opponent_community_club_id?: string;
  opponent_logo_key?: string;
  is_home: boolean;
  competition_name?: string;
  home_score?: number;
  away_score?: number;
  result_status: 'scheduled' | 'live' | 'final' | 'postponed' | 'cancelled';
}

// OpponentResult is one hit from the club directory opponent picker. Mirrors
// opponentResult in internal/api/fixtures.go (a slice of the portal directory).
export interface OpponentResult {
  source: 'go' | 'legacy' | 'community';
  name: string;
  sport?: string;
  municipality?: string;
  slug?: string; // Go org reference
  logo_object_key?: string; // Go org / community-club logo (preview)
  legacy_club_id?: number; // legacy reference
  community_club_id?: string; // crowd-sourced community-club reference
}

export interface MatchEvent {
  id: string;
  minute?: number | null;
  kind: string;
  home_team?: boolean | null;
  actor_member_id?: string;
  note?: string;
  created_at: string;
}

const base = (slug: string) =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/fixtures`;

// opponentLogoUrl resolves a fixture's opponent logo to a displayable URL, or
// null when there's nothing to show (caller renders a monogram). Legacy clubs
// build a URL against the legacy CDN; everything else is a public object key on
// our CDN. Mirrors the portal's resolveLogo precedence.
export function opponentLogoUrl(f: Fixture): string | null {
  if (f.opponent_legacy_club_id != null) return legacyLogoUrl(f.opponent_legacy_club_id);
  if (f.opponent_logo_key) return `${config.cdnBase}/${f.opponent_logo_key}`;
  return null;
}

// directoryLogoUrl resolves a picker result's logo for the live preview.
export function directoryLogoUrl(r: OpponentResult): string | null {
  if (r.source === 'legacy') {
    return r.legacy_club_id != null ? legacyLogoUrl(r.legacy_club_id) : null;
  }
  if (r.logo_object_key) return `${config.cdnBase}/${r.logo_object_key}`;
  return null;
}

export async function getFixture(
  token: string | null,
  slug: string,
  activityId: string,
): Promise<Fixture> {
  return apiFetch<Fixture>(`${base(slug)}/${activityId}`, { token });
}

export async function searchOpponents(
  token: string | null,
  slug: string,
  query: string,
  filters: { country?: string; sport?: string } = {},
): Promise<OpponentResult[]> {
  const qs = new URLSearchParams({ q: query });
  if (filters.country) qs.set('country', filters.country);
  if (filters.sport) qs.set('sport', filters.sport);
  const res = await apiFetch<{ results: OpponentResult[] }>(
    `${base(slug)}/opponents/search?${qs.toString()}`,
    { token },
  );
  return res.results;
}

export interface SetOpponentInput {
  opponent_name: string;
  is_home: boolean;
  competition_name?: string;
  // Optional directory match — pick one to reuse a known club's logo. Precedence
  // server-side: org_slug > legacy_club_id > community_club_id.
  opponent_org_slug?: string;
  opponent_legacy_club_id?: number;
  opponent_community_club_id?: string;
}

export async function setOpponent(
  token: string | null,
  slug: string,
  activityId: string,
  input: SetOpponentInput,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/${activityId}/opponent`, {
    token,
    method: 'PUT',
    body: input,
  });
}

export async function recordResult(
  token: string | null,
  slug: string,
  activityId: string,
  homeScore: number,
  awayScore: number,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/${activityId}/result`, {
    token,
    method: 'PUT',
    body: { home_score: homeScore, away_score: awayScore },
  });
}

// uploadOpponentLogo posts a crest via multipart/form-data. Hand-rolled fetch
// keeps the multipart boundary intact (apiFetch forces JSON). The server sniffs
// the content type from the bytes and rejects unsupported ones.
export async function uploadOpponentLogo(
  token: string | null,
  slug: string,
  activityId: string,
  file: File,
): Promise<void> {
  const form = new FormData();
  form.append('logo', file);
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${config.apiURL}${base(slug)}/${activityId}/logo`, {
    method: 'POST',
    headers,
    body: form,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`logo upload failed: ${res.status} ${text}`);
  }
}

export async function deleteOpponentLogo(
  token: string | null,
  slug: string,
  activityId: string,
): Promise<void> {
  await apiFetch<void>(`${base(slug)}/${activityId}/logo`, { token, method: 'DELETE' });
}

export type ReportOpponentReason =
  | 'wrong_logo'
  | 'wrong_name'
  | 'duplicate'
  | 'offensive'
  | 'other';

// reportOpponentLogo flags a crowd-sourced community club (wrong / offensive /
// duplicate crest) — the safety valve for the shared directory. Internal-ops
// reviews reported rows.
export async function reportOpponentLogo(
  token: string | null,
  slug: string,
  communityClubId: string,
  reason: ReportOpponentReason,
  note?: string,
): Promise<void> {
  const body: { reason: ReportOpponentReason; note?: string } = { reason };
  if (note) body.note = note;
  await apiFetch<void>(
    `${base(slug)}/opponents/${encodeURIComponent(communityClubId)}/report`,
    { token, method: 'POST', body },
  );
}

export async function listMatchEvents(
  token: string | null,
  slug: string,
  activityId: string,
): Promise<MatchEvent[]> {
  const res = await apiFetch<{ events: MatchEvent[] }>(`${base(slug)}/${activityId}/events`, {
    token,
  });
  return res.events;
}

export interface AppendMatchEventInput {
  minute?: number;
  kind: string;
  home_team?: boolean;
  actor_member_id?: string;
  note?: string;
}

export async function appendMatchEvent(
  token: string | null,
  slug: string,
  activityId: string,
  input: AppendMatchEventInput,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`${base(slug)}/${activityId}/events`, {
    token,
    method: 'POST',
    body: input,
  });
}

// MatchDetailsValue is the editor state for a match activity's fixture — the
// "game UI" the schedule form reveals when activity_type is `match`. It's the
// SPA-side working copy that the opponent picker, home/away toggle, competition
// field, and logo control all read and write. `logoPreviewUrl` is display-only
// (a directory pick's logo, an already-stored crest, or an object URL for a
// pending file); persistence ignores it. The three logo sources (orgSlug /
// legacyClubId / logoFile) are mutually exclusive, mirroring the server.
export interface MatchDetailsValue {
  opponentName: string;
  isHome: boolean;
  competitionName: string;
  // Directory match — at most one is set. Precedence server-side:
  // orgSlug > legacyClubId > communityClubId.
  orgSlug: string; // '' = no Go-org match
  legacyClubId: number | null; // null = no legacy match
  communityClubId: string; // '' = no community-club match
  // Pending manual crest upload (held until the activity exists), and the
  // edit-only flag that removes an already-stored crest.
  logoFile: File | null;
  clearLogo: boolean;
  // Display-only: what the logo control should show right now.
  logoPreviewUrl: string | null;
}

export function emptyMatchDetails(): MatchDetailsValue {
  return {
    opponentName: '',
    isHome: true,
    competitionName: '',
    orgSlug: '',
    legacyClubId: null,
    communityClubId: '',
    logoFile: null,
    clearLogo: false,
    logoPreviewUrl: null,
  };
}

// matchDetailsFromFixture seeds the editor from a loaded fixture (edit path).
export function matchDetailsFromFixture(f: Fixture): MatchDetailsValue {
  return {
    opponentName: f.opponent_name ?? '',
    isHome: f.is_home,
    competitionName: f.competition_name ?? '',
    orgSlug: f.opponent_org_slug ?? '',
    legacyClubId: f.opponent_legacy_club_id ?? null,
    communityClubId: f.opponent_community_club_id ?? '',
    logoFile: null,
    clearLogo: false,
    logoPreviewUrl: opponentLogoUrl(f),
  };
}

// hasOpponent reports whether the editor carries enough to persist a fixture.
// A `match` activity with no opponent yet is valid (a TBD fixture) — we just
// skip the setOpponent call rather than reject the save.
export function hasOpponent(d: MatchDetailsValue): boolean {
  return d.opponentName.trim() !== '';
}

// persistMatchDetails applies the editor state to an existing activity. Order
// matters and mirrors the server's mutual-exclusivity rules: setOpponent first
// (a directory ref clears any uploaded crest; no ref preserves it), then the
// logo step (a manual upload clears directory refs; an explicit clear removes
// the crest). Caller guarantees the activity exists.
export async function persistMatchDetails(
  token: string | null,
  slug: string,
  activityId: string,
  d: MatchDetailsValue,
): Promise<void> {
  // Build conditionally — exactOptionalPropertyTypes forbids assigning an
  // explicit `undefined` to the optional fields.
  const input: SetOpponentInput = {
    opponent_name: d.opponentName.trim(),
    is_home: d.isHome,
  };
  const competition = d.competitionName.trim();
  if (competition) input.competition_name = competition;
  if (d.orgSlug) {
    input.opponent_org_slug = d.orgSlug;
  } else if (d.legacyClubId != null) {
    input.opponent_legacy_club_id = d.legacyClubId;
  } else if (d.communityClubId) {
    input.opponent_community_club_id = d.communityClubId;
  }
  await setOpponent(token, slug, activityId, input);
  if (d.logoFile) {
    await uploadOpponentLogo(token, slug, activityId, d.logoFile);
  } else if (d.clearLogo) {
    await deleteOpponentLogo(token, slug, activityId);
  }
}
